import { useMemo, useState, useCallback, useEffect } from 'react';
import { Colors } from '@mercator-gcp/grout';
import { Marker, Polygon } from '@react-google-maps/api';
import useSupercluster from 'use-supercluster';
import { Map, MapProps } from 'components/elements/Map';
import MapCluster from 'components/elements/Map/MapCluster';
import PolygonDrawing from 'components/widgets/ClusterMap/PolygonDrawing';
import ProjectClusters from 'components/widgets/ClusterMap/ProjectClusters';
import SelectedPolygon from 'components/widgets/ClusterMap/SelectedPolygon';
import ProjectListOverlay from 'components/widgets/ProjectListOverlay';
import { useProjectMapPins } from 'hooks/projectMap/useMapPins';
import { useParcelPolygon } from 'hooks/projectMap/useParcelPolygons';
import { useStreetViewVisibility } from 'hooks/projectMap/useStreetViewVisibility';
import {
  MapTypeId,
  useUserPreferredMapType,
} from 'hooks/projectMap/useUserPreferredMapType';
import {
  useDebouncedSetCenterQuery,
  useProjectMapQueryParams,
} from 'hooks/useProjectMap';
import { useToast } from 'hooks/useToast';
import {
  MapRef,
  useGlobalGoogleMapPin,
  useIsPolygonDrawingEnabled,
  useMapCenter,
  useMapState,
  useSelectedMapMarkerAddress,
  useSelectedClusterId,
  useSelectedPolygonBoundsOnMap,
} from 'state/map';
import { useMapBoundsState } from 'state/mapSettings';
import { useAddressQuery, useZoomQuery, useCoordinatesQuery } from 'state/queryParams';
import { trackMapClick } from 'utils/analytics';
import { MapClickClickActions, ProjectGroupTypes } from 'utils/analytics/constants';
import {
  DEFAULT_MAP_ZOOM,
  MAX_MAP_ZOOM,
  SUPERCLUSTER_RADIUS,
  ZOOM_THRESHOLD,
} from 'utils/constants';
import {
  getBoundingBoxesFromPolygon,
  getLatLngBoundsFromPolygon,
  getPolygonFromBoundingBox,
} from 'utils/polygons';
import type { BoundingBox, IdentifiableCoords } from 'types/map';
import type { PolygonType } from 'types/Polygon';

type ClusterMapProps = MapProps & {
  selectedMarker?: string;
  onChangeBounds?(bounds: number[], zoom: number): void;
  loading?: boolean;
  defaultToSateliteView?: boolean;
  highlightCurrentPolygon?: boolean;
  allowPolygonInteraction?: boolean;
  showLoadingSpinner?: boolean;
  saveMapTypePreference?: boolean;
};

export const ClusterMap = ({
  mapProps,
  defaultToSateliteView = false,
  saveMapTypePreference = false,
  highlightCurrentPolygon = false,
  allowPolygonInteraction = true,
  showLoadingSpinner = true, // Default to true for backwards compatibility
  ...props
}: ClusterMapProps) => {
  const { mapMarkers: clusterPointsList } = useProjectMapPins();
  const [zoomQuery, setZoomQuery] = useZoomQuery();
  const { userPreferredMapType, setUserPreferredMapType } = useUserPreferredMapType();
  const {
    mapRef,
    mapLoaded,
    updateCenter,
    updateParcelProjects,
    selectedMapType,
    setSelectedMapType,
  } = useMapState();
  const [addressQuery] = useAddressQuery();
  const [, setSelectedMapMarkerAddress] = useSelectedMapMarkerAddress();
  const [, setSelectedClusterId] = useSelectedClusterId();
  const [selectedPolygonBoundsOnMap, setSelectedPolygonBoundsOnMap] =
    useSelectedPolygonBoundsOnMap();
  const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM);
  const [hasFinishedInitialLoad, setHasFinishedInitialLoad] = useState(false);

  const { googleMapPin } = useGlobalGoogleMapPin();
  const { renderLoadingToast, closeToast, renderToast } = useToast();
  const [toastId, setToastId] = useState('');
  const [isPolygonDrawingEnabled] = useIsPolygonDrawingEnabled();
  const [activeMarker, setActiveMarker] = useState<IdentifiableCoords>(
    {} as IdentifiableCoords,
  );
  const { coordinatesQuery, coordinatesAsLatLng } = useCoordinatesQuery();
  const { clearProjectMapCoordinatePin } = useProjectMapQueryParams();
  const [mapCenterState, setMapCenterState] = useState('');
  const { mapBoundsAsCardinalDirections, updateBoundsState } = useMapBoundsState();
  const [mapCenterGlobal] = useMapCenter();

  useDebouncedSetCenterQuery(mapCenterState);
  useStreetViewVisibility(mapRef as MapRef);

  useEffect(() => {
    /* Pan to polygon if map center changed with selected polygons */
    if (mapRef && mapCenterGlobal && selectedPolygonBoundsOnMap) {
      const polygonBounds = getPolygonFromBoundingBox(selectedPolygonBoundsOnMap);
      const latLngBounds = getLatLngBoundsFromPolygon(polygonBounds);
      if (!latLngBounds) {
        return;
      }
      mapRef.fitBounds(latLngBounds);
    }
  }, [mapRef, mapCenterGlobal]);

  const localZoom = useCallback(() => {
    return mapRef?.getZoom() || DEFAULT_MAP_ZOOM;
  }, [mapRef]);

  const {
    data: parcelPolygon,
    isFetching,
    error: parcelPolygonError,
  } = useParcelPolygon();

  const closeOverlay = () => {
    setActiveMarker({} as IdentifiableCoords);
    setSelectedClusterId('');
  };

  const setCenterAndZoomFromMap = useCallback(() => {
    if (!mapRef) {
      return;
    }
    updateBoundsState(mapRef.getBounds());
    // update centerQuery param
    const mapCenter = mapRef.getCenter()?.toUrlValue();
    if (typeof mapCenter === 'string') {
      setMapCenterState(mapCenter);
    }

    // update zoomQuery param
    setZoom(mapRef.getZoom() || DEFAULT_MAP_ZOOM);
    setZoomQuery(String(mapRef?.getZoom()));
  }, [mapRef, zoomQuery, setZoomQuery, updateBoundsState]);

  const freezeZoom = () => {
    mapRef?.setOptions({ scrollwheel: false }); // Disable map zooming when scrolling over the list
  };

  const resumeZoom = () => {
    mapRef?.setOptions({ scrollwheel: true }); // Re-enable map zooming when not scrolling over the list
  };

  const superclusterBounds: [number, number, number, number] = useMemo(() => {
    if (!mapBoundsAsCardinalDirections || !mapLoaded) {
      return [0, 0, 0, 0];
    }
    const { north, east, south, west } = mapBoundsAsCardinalDirections;

    return [west, south, east, north];
  }, [mapBoundsAsCardinalDirections, mapLoaded]);

  const onMapTypeIdChanged = useCallback(() => {
    const map = mapRef;
    const mapType = map?.getMapTypeId();
    if (mapType) {
      setSelectedMapType(mapType);
      if (saveMapTypePreference) {
        setUserPreferredMapType(mapType as MapTypeId); // Save the preference
      }
    }
  }, [mapRef, saveMapTypePreference, setSelectedMapType, setUserPreferredMapType]);

  const onMapClick = () => {
    // Clear project map coordinate pin when user clicks on the map.
    clearProjectMapCoordinatePin();
    closeOverlay();
    setSelectedClusterId('');
  };

  const mapTypeId = useMemo(() => {
    if (saveMapTypePreference && userPreferredMapType) {
      return userPreferredMapType;
    }

    if (defaultToSateliteView) {
      return google.maps.MapTypeId.HYBRID;
    }

    return selectedMapType || google.maps.MapTypeId.ROADMAP;
  }, [
    saveMapTypePreference,
    userPreferredMapType,
    defaultToSateliteView,
    selectedMapType,
  ]);

  const onZoomChanged = useCallback(() => {
    closeOverlay();
    setSelectedClusterId('');
  }, [closeOverlay, setSelectedClusterId]);

  const onTilesLoaded = () => {
    if (!hasFinishedInitialLoad) {
      setHasFinishedInitialLoad(true);

      const map = mapRef;
      map?.setMapTypeId(mapTypeId);

      setCenterAndZoomFromMap();
    }
  };

  const createPointGeoJSON = ({
    lat,
    lng,
    id,
    address,
  }: IdentifiableCoords & { address: string }) => ({
    type: 'Feature',
    properties: {
      id,
      address,
      cluster: false,
    },
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
  });

  const { clusters } = useSupercluster({
    points: clusterPointsList.map(createPointGeoJSON),
    bounds: superclusterBounds,
    zoom,
    options: { maxZoom: MAX_MAP_ZOOM, radius: SUPERCLUSTER_RADIUS },
  });

  const markerClick = useCallback(
    (point: IdentifiableCoords & { address: string }) => {
      // Clear any polygons that are selected
      updateParcelProjects([]);
      updateCenter({ lat: point.lat, lng: point.lng });
      setActiveMarker(point); // set active marker ID
      setSelectedMapMarkerAddress(point.address);
      setSelectedClusterId(''); // Clear clusterId when marker is clicked
    },
    [
      setSelectedMapMarkerAddress,
      setSelectedClusterId,
      updateCenter,
      addressQuery,
      updateParcelProjects,
    ],
  );

  const onClusterClick = useCallback(
    ({
      lat,
      lng,
      count,
      index,
    }: {
      lat: number;
      lng: number;
      count: number;
      index: string;
    }) => {
      updateCenter({ lat, lng });

      // If count is low enough, load the projects on the popup menu instead
      // of zooming the user in
      if (count <= 5) {
        updateParcelProjects([]);
        // Set clusterId and open ProjectListOverlay
        setSelectedClusterId(index);
        setSelectedMapMarkerAddress(''); // Clear address when cluster is clicked
        setActiveMarker({ lat, lng, id: index } as IdentifiableCoords & {
          address: string;
        });
      } else {
        // We want to zoom into the cluster when clicked.
        // And center the map on the cluster coordinates.
        const newZoomLevel = Math.min(localZoom() + 3, ZOOM_THRESHOLD);
        mapRef?.setZoom(newZoomLevel);
      }

      trackMapClick({
        groupId: '0',
        groupType: ProjectGroupTypes.UNCATEGORIZED,
        clickAction: MapClickClickActions.MAP_CLUSTER,
      });
    },
    [localZoom, updateCenter, setSelectedClusterId, setSelectedMapMarkerAddress],
  );

  const onPolygonClick = (polygonBounds: BoundingBox) => {
    if (!allowPolygonInteraction) {
      return;
    }
    // Clear address if we click on a new polygon
    setSelectedMapMarkerAddress('');
    setSelectedPolygonBoundsOnMap(polygonBounds);
  };

  useEffect(() => {
    let currentToastId = toastId;

    if (highlightCurrentPolygon) {
      return;
    }

    if (isFetching && !currentToastId) {
      // Show the toast if not already shown
      currentToastId = renderLoadingToast({
        toastTitle: 'Loading parcel polygons...',
        status: 'success',
        label: 'Parcel outlines are loading....',
      });
      setToastId(currentToastId);
    } else if (!isFetching && currentToastId) {
      // Close the toast when fetching is done
      closeToast(currentToastId);
      setToastId(''); // Reset the toast ID
      if (parcelPolygonError) {
        renderToast({
          toastTitle: 'failed_to_load_parcel_outlines',
          status: 'error',
          title: 'Error',
          label: 'Parcels failed to load',
        });
      }
    }
  }, [isFetching, toastId, renderLoadingToast, renderToast]);

  const showClusters = useMemo(() => {
    return zoom < ZOOM_THRESHOLD;
  }, [zoom]);

  return (
    <Map
      {...props}
      showLoadingSpinner={showLoadingSpinner}
      mapProps={{
        ...mapProps,
        onIdle: setCenterAndZoomFromMap,
        onZoomChanged,
        onTilesLoaded,
        onMapTypeIdChanged,
        onClick: onMapClick,
        // Tilt value 0 causes the map to always use a 0° overhead view
        // regardless of the zoom level.
        tilt: 0,
        options: {
          minZoom: DEFAULT_MAP_ZOOM,
          maxZoom: MAX_MAP_ZOOM,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          scaleControl: true,
          streetViewControl: true,
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          rotateControl: false,
          fullscreenControl: false,
          ...(allowPolygonInteraction ? {} : { mapTypeId }),
        },
      }}
    >
      {mapLoaded && (
        <>
          {showClusters && (
            <ProjectClusters
              onClusterClick={onClusterClick}
              activeMarker={activeMarker}
            />
          )}
          {!showClusters &&
            clusters.map((cluster) => {
              const [longitude, latitude] = cluster.geometry.coordinates;
              const { cluster: isCluster, point_count: pointCount } = cluster.properties;
              if (isCluster) {
                return (
                  <MapCluster
                    clusterKey={`cluster-${cluster.id}`}
                    lat={latitude}
                    lng={longitude}
                    label={pointCount.toString()}
                    onClick={() => {
                      const targetZoom = (localZoom() || DEFAULT_MAP_ZOOM) + 3; // Adjust the zoom level as needed
                      const currentZoom = localZoom() || DEFAULT_MAP_ZOOM;

                      if (currentZoom >= ZOOM_THRESHOLD) {
                        // zoom in to the cluster immediately
                        mapRef?.setZoom(MAX_MAP_ZOOM);
                      } else {
                        // zoom in to the cluster
                        mapRef?.setZoom(targetZoom);
                      }
                      updateCenter({ lat: latitude, lng: longitude });

                      trackMapClick({
                        groupId: '0',
                        groupType: ProjectGroupTypes.UNCATEGORIZED,
                        clickAction: MapClickClickActions.MAP_CLUSTER,
                      });
                    }}
                  />
                );
              }

              return (
                <Marker
                  key={cluster.properties.id}
                  onClick={() => {
                    markerClick({
                      lat: latitude,
                      lng: longitude,
                      id: cluster.properties.id,
                      address: cluster.properties.address,
                    });
                    trackMapClick({
                      groupId: '0',
                      groupType: ProjectGroupTypes.UNCATEGORIZED,
                      clickAction: MapClickClickActions.MAP_MARKER,
                    });
                  }}
                  position={{
                    lat: latitude,
                    lng: longitude,
                  }}
                  icon={{
                    url:
                      activeMarker?.id === cluster.properties.id
                        ? '/map-icon-selected.svg'
                        : '/map-icon.svg',
                    anchor: new google.maps.Point(13.5, 34),
                  }}
                />
              );
            })}

          <SelectedPolygon
            onClick={(bounds: BoundingBox) => {
              onPolygonClick(bounds);
            }}
          />
          {localZoom() >= ZOOM_THRESHOLD &&
            parcelPolygon?.map((polygon: PolygonType) => {
              const polygonBounds = getBoundingBoxesFromPolygon(polygon);

              return (
                <Polygon
                  key={`map-polygon-${polygonBounds}`}
                  paths={polygon}
                  options={{
                    strokeColor: Colors.ForestGreen900,
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                    fillOpacity: 0,
                    fillColor: Colors.ForestGreen900,
                  }}
                  onClick={() => {
                    onPolygonClick(polygonBounds);
                    trackMapClick({
                      groupId: '0',
                      groupType: ProjectGroupTypes.UNCATEGORIZED,
                      clickAction: MapClickClickActions.PARCEL_POLYGON,
                    });
                  }}
                />
              );
            })}
          {googleMapPin.longitude && googleMapPin.latitude && (
            <Marker
              position={{ lat: googleMapPin.latitude, lng: googleMapPin.longitude }}
            />
          )}
          {coordinatesQuery && (
            <Marker
              position={{ lat: coordinatesAsLatLng.lat, lng: coordinatesAsLatLng.lng }}
              clickable={false}
              zIndex={999}
            />
          )}
          {Object.keys(activeMarker).length > 0 && (
            <ProjectListOverlay
              activeMarker={activeMarker}
              freezeZoom={freezeZoom}
              resumeZoom={resumeZoom}
              setActiveMarker={setActiveMarker}
              setSelectedMapMarkerAddress={setSelectedMapMarkerAddress}
              setSelectedClusterId={setSelectedClusterId}
            />
          )}
          {isPolygonDrawingEnabled && <PolygonDrawing />}
        </>
      )}
    </Map>
  );
};
